import executeSearch from '../actions/executeSearch';
import resetViewState from '../actions/resetViewState';
import selectInlineSearchAction from '../actions/selectInlineSearchAction';
import setIsSuggesting from '../actions/setIsSuggesting';
import shiftInlineSearchHighlight from '../actions/shiftInlineSearchHighlight';
import { disposeInlineSearchPopup, showInlineSearchPopup } from '../components/InlineSearchPopup';
import datapoints, { logCortanaUsage } from '../datapoints';
import cortanaComposeStore from '../store/cortanaComposeStore';
import CortanaComposeResult from '../store/schema/CortanaComposeResult';
import ScrollAdapter from 'owa-scroll-adapter/lib/ScrollAdapter';
import { Editor } from 'roosterjs-editor-core';
import { PickerDataProvider } from 'roosterjs-plugin-picker';

const RESIZE_EVENT_NAME = 'resize';

export default class CortanaComposeDataProvider implements PickerDataProvider {
    private commitSelection: (element: HTMLElement) => void;
    private setIsSuggesting: (isSuggesting: boolean) => void;
    private popupDiv: HTMLDivElement;
    public name = 'CortanaCompose';
    private editor: Editor;

    constructor(private scrollAdapter: ScrollAdapter) {}

    onInitalize(
        commitMentionCallback: (element: HTMLElement) => void,
        isSuggestingCallback: (isSuggesting: boolean) => void,
        editor?: Editor
    ) {
        resetViewState({
            insertContent: this.insertHtmlContentIntoEditor,
            dismissSuggesting: this.dismissSelection,
        });
        window.addEventListener(RESIZE_EVENT_NAME, this.onScrollOrResize);
        this.scrollAdapter.addScrollEventListener(this.onScrollOrResize);
        const divElement = document.createElement('div');
        divElement.id = 'inlineSearchPopup';
        document.body.appendChild(divElement);
        this.popupDiv = divElement;

        this.commitSelection = commitMentionCallback;
        this.setIsSuggesting = isSuggestingCallback;
        this.editor = editor;
    }

    onDispose() {
        window.removeEventListener(RESIZE_EVENT_NAME, this.onScrollOrResize);
        this.scrollAdapter.removeScrollEventListener(this.onScrollOrResize);
        if (this.popupDiv) {
            disposeInlineSearchPopup(this.popupDiv);
            document.body.removeChild(this.popupDiv);
        }
        this.popupDiv = null;
    }

    onIsSuggestingChanged = (isSuggesting: boolean) => {
        if (cortanaComposeStore.viewState.isPopupShown && !isSuggesting) {
            logCortanaUsage(
                datapoints.CortanaComposeSearchDismissed.datapoint.name,
                datapoints.CortanaComposeSearchDismissed.datapoint.customData(
                    cortanaComposeStore.currentSearchCorrelationId
                )
            );
        } else if (!cortanaComposeStore.viewState.isPopupShown && isSuggesting) {
            logCortanaUsage(
                datapoints.CortanaComposeInlineSearchOpened.datapoint.name,
                datapoints.CortanaComposeInlineSearchOpened.datapoint.customData(
                    cortanaComposeStore.currentSearchCorrelationId
                )
            );
        }
        setIsSuggesting(isSuggesting);

        // Since we're not using the user's message body to provide search context, there's no reason to remove the cached search results
        // when changing the suggesting state, so remove this for now. Re-add this once the Cortana service we're using is in the compliance
        // boundary and we can start sending the message body as context
        // setCachedDoradoSearchResults(null); // clear search results cache
    };

    queryStringUpdated = (queryString: string) => {
        executeSearch(queryString, this.editor.getTextContent());
    };

    selectOption = () => {
        const viewState = cortanaComposeStore.viewState;
        const sectionNames = [...viewState.resultsShown.keys()];
        const selectedSection = sectionNames[viewState.selectedSectionIndex];
        const selectedItem = viewState.resultsShown.get(selectedSection)[
            viewState.selectedIndexInSection
        ] as CortanaComposeResult;

        if (!selectedItem) {
            setIsSuggesting(false);
        } else {
            this.dismissSelection();
            selectInlineSearchAction(selectedItem);
        }
    };

    shiftHighlight = (isDown: boolean) => {
        shiftInlineSearchHighlight(isDown);
    };

    onRemove = (nodeRemoved: Node, isBackspace: boolean) => {
        return null;
    };

    setCursorPoint = (targetPoint: { x: number; y: number }, buffer: number) => {
        showInlineSearchPopup(targetPoint, buffer, this.popupDiv);
    };

    private onScrollOrResize = () => {
        if (cortanaComposeStore.viewState.isSuggesting) {
            setIsSuggesting(false);
        }
    };

    private insertHtmlContentIntoEditor = (elementToInsert: HTMLElement) => {
        const wrapper = document.createElement('div');
        wrapper.appendChild(elementToInsert);
        this.editor.insertContent(wrapper.innerHTML);
    };

    private dismissSelection = () => {
        this.commitSelection(document.createElement('span'));
        this.setIsSuggesting(false);
    };
}
